// Textos compactos de condiciones y acciones para listados (tabla/cards) sin abrir el detalle.
import type { TFunction } from 'i18next';
import type { RuleAction, RuleCondition, RulesModel } from '../models/Rules';

const MAX_LINE = 160;

const truncate = (s: string, max = MAX_LINE) => (s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s);

const formatAction = (a: RuleAction, t: TFunction<'rulesModule'>): string => {
  if (a.action === 'no_enviar') {
    return t('summary.action_excluded');
  }
  if (a.action === 'courier_integration_id') {
    const parts: string[] = [t('summary.action_send_courier')];
    if (a.value != null && a.value !== '') {
      parts.push(t('summary.courier_id', { id: String(a.value) }));
    }
    if (a.courier_service_id != null) {
      parts.push(t('summary.service_id', { id: a.courier_service_id }));
    }
    return parts.join(' · ');
  }
  return a.action;
};

const formatActions = (actions: RuleAction[] | undefined, t: TFunction<'rulesModule'>): string => {
  if (!actions?.length) {
    return t('summary.no_actions');
  }
  return actions.map((a) => formatAction(a, t)).join(' · ');
};

const formatCondition = (c: RuleCondition, t: TFunction<'rulesModule'>): string => {
  const op = c.operator === '=' ? t('summary.op.eq') : c.operator === '!=' ? t('summary.op.neq') : c.operator;
  return `${c.field} ${op} ${c.value}`.trim();
};

/**
 * Línea 1: condiciones (o aviso de vacío). Línea 2: "Entonces" + acción(es).
 */
export function getRuleSummaryLines(rule: RulesModel, t: TFunction<'rulesModule'>): { line1: string; line2: string } {
  const conditions = rule.rules?.conditions ?? [];
  const matchIsAny = rule.rules?.match_type === 'any';
  const matchPrefix = matchIsAny ? t('summary.match_any_short') : t('summary.match_all_short');

  let line1: string;
  if (conditions.length === 0) {
    line1 = t('summary.no_conditions');
  } else {
    const parts = conditions.map((c) => formatCondition(c, t));
    const list = parts.join(' · ');
    line1 = truncate(`${matchPrefix} ${list}`);
  }

  const line2 = `${t('summary.then_prefix')}: ${formatActions(rule.actions, t)}`;

  return { line1, line2: truncate(line2, 120) };
}
